'use client';

import { useMemo, useState } from 'react';
import BlogCard from '@/components/blocks/blog-card';
import { blogCategories, blogPosts } from '@/data/blog-posts';

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export default function BlogPageClient() {
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [searchTerm, setSearchTerm] = useState('');

  const listingPosts = useMemo(
    () =>
      blogPosts.map((post) => ({
        ...post,
        date: dateFormatter.format(new Date(post.date)),
      })),
    []
  );

  const filteredPosts = listingPosts.filter((post) => {
    const matchesCategory = selectedCategory === 'Toutes' || post.category === selectedCategory;
    const matchesSearch =
      searchTerm === '' ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="text-center mb-16 pt-32">
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Blog Sante & Securite</h1>
        <p className="text-lg text-white/70 max-w-3xl mx-auto">
          Retrouvez nos derniers articles, conseils et actualites sur la sante, la securite et les conditions de travail.
        </p>
      </div>

      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {blogCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'border border-accent/30 bg-accent/15 text-white'
                    : 'border border-white/10 bg-white/5 text-white/80 hover:border-accent/25 hover:bg-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="w-full md:w-auto">
            <input
              type="text"
              placeholder="Rechercher un article..."
              className="w-full md:w-72 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder:text-white/40 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredPosts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <BlogCard key={post.id} {...post} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-white/70 text-lg">Aucun article ne correspond a vos criteres de recherche.</p>
          <button
            onClick={() => {
              setSelectedCategory('Toutes');
              setSearchTerm('');
            }}
            className="mt-4 text-white/80 font-medium hover:text-accent"
          >
            Reinitialiser les filtres
          </button>
        </div>
      )}

      <div className="mt-16 rounded-3xl border border-accent/20 bg-white/5 p-8 shadow-sm backdrop-blur">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Restez informes !</h2>
          <p className="text-white/75 mb-6">
            Abonnez-vous a notre newsletter pour recevoir nos derniers articles et actualites sur la sante et la
            securite au travail.
          </p>

          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Votre adresse email"
              className="flex-grow rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder:text-white/40 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
              required
            />
            <button
              type="submit"
              className="rounded-xl bg-accent px-6 py-2 text-base font-semibold text-black hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/30 transition-colors duration-200"
            >
              S&apos;abonner
            </button>
          </form>

          <p className="text-xs text-white/60 mt-4">
            En vous inscrivant, vous acceptez de recevoir nos emails. Vous pourrez vous desinscrire a tout moment.
          </p>
        </div>
      </div>
    </div>
  );
}
